import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  LucideArrowDownWideNarrow,
  LucideArrowLeft,
  LucideBan,
  LucideCheck,
  LucideChevronRight,
  LucideEllipsis,
  LucideMaximize2,
  LucideUndo2
} from '@lucide/angular';
import { Subscription } from 'rxjs';
import { TimerOverlayComponent } from '../../timer/timer-overlay.component';
import { computeActiveElapsedMs, computePlanMs, formatElapsedMs, formatOverrunWords } from '../../timer/timer-elapsed.util';
import { AllocationRow, DayStore } from '../day.store';
import { resolveCycleDisplayLabel, resolveScheduleGroupCycle } from '../day-status-cycles.util';
import { rebuildOrderedIdsForSubset } from '../order.util';
import {
  groupPageCommands,
  parseGroupOrdinal,
  resolveScheduleGroupLabel
} from '../schedule-group.util';
import {
  GroupRow,
  buildVisibleGroupRows,
  buildGroupCapsules,
  buildGroupMetaLine,
  countNotThisCycle,
  splitCapsulesByCycle
} from '../group-page.util';

@Component({
  selector: 'app-group-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    CdkDropList,
    CdkDrag,
    TimerOverlayComponent,
    LucideArrowLeft,
    LucideArrowDownWideNarrow,
    LucideBan,
    LucideCheck,
    LucideChevronRight,
    LucideEllipsis,
    LucideMaximize2,
    LucideUndo2
  ],
  templateUrl: './group-page.component.html',
  styleUrls: ['./group-page.component.scss']
})
export class GroupPageComponent implements OnInit, OnDestroy {
  readonly store = inject(DayStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);

  readonly day = this.store.day;
  readonly dayLoading = this.store.dayLoading;
  readonly dayError = this.store.dayError;
  readonly blockMutationSubmitting = this.store.blockMutationSubmitting;
  readonly blockMutationError = this.store.blockMutationError;
  readonly startSubmitting = this.store.startSubmitting;
  readonly activityReorderSubmitting = this.store.activityReorderSubmitting;
  readonly activityReorderError = this.store.activityReorderError;
  readonly notThisCycleError = this.store.notThisCycleError;
  readonly dayStatusOldestFirst = this.store.dayStatusOldestFirst;
  readonly enabledScheduleGroups = this.store.enabledScheduleGroups;
  readonly dayStatusCycles = this.store.dayStatusCycles;
  readonly workCycleLabel = this.store.workCycleLabel;
  readonly lifeCycleLabel = this.store.lifeCycleLabel;
  readonly listDragStartDelay = { touch: 160, mouse: 0 };
  readonly runningRowAnchorId = 'group-page-running-row';
  readonly runningStripAnchorId = 'group-page-running-strip';

  readonly groupOrdinal = signal<ReturnType<typeof parseGroupOrdinal>>(parseGroupOrdinal(this.route.snapshot.paramMap.get('ordinal')));
  readonly expandedInlineTimer = signal(false);
  readonly expandedRemoteTimer = signal(false);
  readonly elapsedMs = signal(0);
  readonly openMenuActivityId = signal<string | null>(null);
  readonly resolveScheduleGroupLabel = resolveScheduleGroupLabel;

  private readonly timerPreferenceKey = 'lumen.groupPage.timerExpanded';
  private readonly inlineTimerExpandButtonSelector = '[data-inline-timer-expand]';
  private readonly remoteTimerExpandButtonSelector = '[data-remote-timer-expand]';
  private timerExpandedPreference = false;
  private forcedTimerOpenForVisit = false;
  private loaded = false;
  private pendingTimerOpenFromQuery = false;
  private elapsedIntervalId: ReturnType<typeof setInterval> | null = null;
  private routeParamSubscription: Subscription | null = null;
  private routeQuerySubscription: Subscription | null = null;

  constructor() {
    effect(() => {
      this.runningBlock();
      this.groupOrdinal();
      this.syncRunningTimerTicker();
      this.syncExpandedTimerPreference();
      this.applyForcedTimerOpenIfPossible();
    });
  }

  readonly selectedGroup = computed(() => {
    const ordinal = this.groupOrdinal();
    if (!ordinal) {
      return null;
    }
    return this.enabledScheduleGroups().find((group) => group.ordinal === ordinal) ?? null;
  });

  readonly runningBlock = computed(() => this.day()?.runningBlock ?? null);
  readonly runningScheduleType = computed(() => {
    const runningBlock = this.runningBlock();
    if (!runningBlock) {
      return null;
    }
    const activity = this.store.activityTypes().find((candidate) => candidate.id === runningBlock.activityTypeId);
    if (activity?.kind === 'TIME_BLOCK') {
      return activity.scheduleType;
    }
    const allocation = this.day()?.allocations.find((candidate) => candidate.activityTypeId === runningBlock.activityTypeId);
    return allocation?.scheduleType ?? null;
  });

  readonly runningGroupLabel = computed(() => {
    const runningOrdinal = this.runningScheduleType();
    if (!runningOrdinal) {
      return null;
    }
    const group = this.enabledScheduleGroups().find((candidate) => candidate.ordinal === runningOrdinal);
    return group ? resolveScheduleGroupLabel(group) : runningOrdinal;
  });

  readonly isRunningGroup = computed(() => {
    const ordinal = this.groupOrdinal();
    return !!ordinal && this.runningScheduleType() === ordinal;
  });

  readonly doneByOrdinal = computed(() => {
    const byOrdinal = new Map<AllocationRow['scheduleType'], boolean>();
    const sections = [this.dayStatusCycles().work, this.dayStatusCycles().life];
    for (const section of sections) {
      for (const group of section.groups) {
        byOrdinal.set(group.ordinal, group.doneThisCycle);
      }
    }
    return byOrdinal;
  });

  readonly capsules = computed(() => buildGroupCapsules(
    this.enabledScheduleGroups(),
    this.doneByOrdinal(),
    this.runningScheduleType()
  ));

  readonly capsulesByCycle = computed(() => splitCapsulesByCycle(this.capsules()));
  readonly workCapsules = computed(() => this.capsulesByCycle().work);
  readonly lifeCapsules = computed(() => this.capsulesByCycle().life);
  readonly showCapsuleSeparator = computed(() => this.workCapsules().length > 0 && this.lifeCapsules().length > 0);

  readonly groupRows = computed<GroupRow[]>(() => {
    const ordinal = this.groupOrdinal();
    if (!ordinal) {
      return [];
    }
    const rows = this.store.getAllocationRowsForScheduleType(ordinal);
    return buildVisibleGroupRows(rows, this.dayStatusOldestFirst());
  });

  readonly runningRow = computed(() => {
    const runningBlock = this.runningBlock();
    if (!runningBlock) {
      return null;
    }
    return this.groupRows().find((row) => row.activityTypeId === runningBlock.activityTypeId) ?? null;
  });

  readonly visibleOpenRows = computed(() => this.groupRows().filter((row) => !row.doneThisCycle && !row.notThisCycle));
  readonly notThisCycleCount = computed(() => countNotThisCycle(this.groupRows()));
  readonly showEmptyMessage = computed(() => this.visibleOpenRows().length === 0);

  readonly groupMetaLine = computed(() => {
    const selectedGroup = this.selectedGroup();
    if (!selectedGroup) {
      return '';
    }
    const visibleRows = this.groupRows();
    const totalCount = visibleRows.length;
    const doneCount = visibleRows.filter((row) => row.doneThisCycle).length;
    return buildGroupMetaLine({
      cycle: resolveScheduleGroupCycle(selectedGroup),
      workCycleLabel: this.workCycleLabel(),
      lifeCycleLabel: this.lifeCycleLabel(),
      doneCount,
      totalCount,
      notThisCycleCount: this.notThisCycleCount(),
      oldestFirst: this.dayStatusOldestFirst()
    });
  });

  readonly runningSlotLabel = computed(() => {
    const runningBlock = this.runningBlock();
    if (!runningBlock) {
      return null;
    }
    const allocation = this.day()?.allocations.find((entry) => entry.activityTypeId === runningBlock.activityTypeId);
    if (!allocation || allocation.slotCount <= 0) {
      return null;
    }
    const slotIndex = Math.min(allocation.slotCount, allocation.completedSlots + 1);
    return `slot ${slotIndex} of ${allocation.slotCount}`;
  });

  readonly runningSummary = computed(() => {
    const runningBlock = this.runningBlock();
    if (!runningBlock) {
      return '';
    }
    const elapsedMs = this.elapsedMs();
    const stateLabel = runningBlock.pausedAt ? 'Paused' : 'Running';
    const bits = [stateLabel];
    const slotLabel = this.runningSlotLabel();
    if (slotLabel) {
      bits.push(slotLabel);
    }
    bits.push(formatElapsedMs(elapsedMs));
    if (!runningBlock.pausedAt) {
      const overrunMs = Math.max(0, elapsedMs - computePlanMs(runningBlock.durationMinutes));
      if (overrunMs > 0) {
        bits.push(formatOverrunWords(overrunMs));
      }
    }
    return bits.join(' · ');
  });

  /** Paused or past the planned slot: the strip switches to the attention colour and says so in words. */
  readonly runningStripAttention = computed(() => {
    const runningBlock = this.runningBlock();
    if (!runningBlock) {
      return false;
    }
    return !!runningBlock.pausedAt || this.elapsedMs() > computePlanMs(runningBlock.durationMinutes);
  });

  readonly runningStripTimeLabel = computed(() => {
    const runningBlock = this.runningBlock();
    if (!runningBlock) {
      return '';
    }
    const elapsedMs = this.elapsedMs();
    const elapsed = formatElapsedMs(elapsedMs);
    if (runningBlock.pausedAt) {
      return `Paused · ${elapsed}`;
    }
    const overrunMs = Math.max(0, elapsedMs - computePlanMs(runningBlock.durationMinutes));
    return overrunMs > 0 ? `${elapsed} · ${formatOverrunWords(overrunMs)}` : elapsed;
  });

  readonly showRemoteTimerStrip = computed(() => !!this.runningBlock() && !this.isRunningGroup());

  ngOnInit(): void {
    this.loadTimerPreference();
    this.applyOrdinalFromParam(this.route.snapshot.paramMap.get('ordinal'));
    this.routeParamSubscription = this.route.paramMap.subscribe((params) => {
      this.applyOrdinalFromParam(params.get('ordinal'));
    });
    this.routeQuerySubscription = this.route.queryParamMap.subscribe((queryParams) => {
      this.pendingTimerOpenFromQuery = queryParams.get('timer')?.trim().toLowerCase() === 'open';
      this.applyForcedTimerOpenIfPossible();
    });
    this.store.ensureLoaded().subscribe(() => {
      this.loaded = true;
      this.ensureSelectedGroupEnabled();
      this.syncExpandedTimerPreference();
      this.applyForcedTimerOpenIfPossible();
    });
    this.syncRunningTimerTicker();
  }

  ngOnDestroy(): void {
    this.routeParamSubscription?.unsubscribe();
    this.routeQuerySubscription?.unsubscribe();
    this.stopElapsedTicker();
  }

  refreshDayError(): void {
    this.store.refreshDayError();
  }

  refreshAllocationError(): void {
    this.store.refreshAllocationError();
  }

  toggleOldestFirst(): void {
    this.store.persistDayStatusOldestFirst(!this.dayStatusOldestFirst());
  }

  onAllocationDrop(event: CdkDragDrop<GroupRow[]>): void {
    const ordinal = this.groupOrdinal();
    if (!ordinal || this.activityReorderSubmitting()) {
      return;
    }
    const draggableIds = this.groupRows()
      .filter((row) => !row.doneThisCycle && !row.notThisCycle)
      .map((row) => row.activityTypeId);
    if (event.previousIndex >= draggableIds.length) {
      return;
    }
    const currentIndex = Math.min(event.currentIndex, Math.max(0, draggableIds.length - 1));
    if (event.previousIndex === currentIndex && !this.dayStatusOldestFirst()) {
      return;
    }
    const nextDraggableIds = [...draggableIds];
    moveItemInArray(nextDraggableIds, event.previousIndex, currentIndex);
    if (this.dayStatusOldestFirst()) {
      this.store.persistDayStatusOldestFirst(false);
    }
    const allIds = this.store.getSortedTimeBlockActivitiesForScheduleType(ordinal).map((activity) => activity.id);
    const orderedIds = rebuildOrderedIdsForSubset(allIds, draggableIds, nextDraggableIds);
    this.store.persistActivityOrder(orderedIds, {
      kind: 'TIME_BLOCK',
      scheduleType: ordinal
    }, 'Failed to reorder activity types');
  }

  navigateToAllocation(activityTypeId: string): void {
    const ordinal = this.groupOrdinal();
    if (!ordinal) {
      return;
    }
    this.router.navigate(['/today/allocation', activityTypeId], {
      queryParams: { from: ordinal }
    });
  }

  openAllocationMenu(event: Event, activityTypeId: string): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.openMenuActivityId() === activityTypeId) {
      this.closeMenu();
      return;
    }
    this.openMenuActivityId.set(activityTypeId);
    setTimeout(() => this.focusMenuItem(activityTypeId));
  }

  closeMenu(restoreFocus = true): void {
    const activityId = this.openMenuActivityId();
    this.openMenuActivityId.set(null);
    if (restoreFocus && activityId) {
      this.focusMenuTrigger(activityId);
    }
  }

  isMenuOpen(activityTypeId: string): boolean {
    return this.openMenuActivityId() === activityTypeId;
  }

  stopRowNavigation(event: Event): void {
    event.stopPropagation();
  }

  startBlock(event: Event, activityTypeId: string): void {
    event.stopPropagation();
    this.store.startBlock(activityTypeId);
  }

  resumePausedBlock(event: Event, activityTypeId: string): void {
    event.stopPropagation();
    this.store.resumePausedBlock(activityTypeId);
  }

  getPausedBlockForActivity(activityTypeId: string) {
    return this.store.getPausedBlockForActivity(activityTypeId);
  }

  canStartBlock(activityTypeId: string): boolean {
    return this.store.canStartBlock(activityTypeId);
  }

  canResumePausedBlock(activityTypeId: string): boolean {
    return this.store.canResumePausedBlock(activityTypeId);
  }

  isStartSubmitting(activityTypeId: string): boolean {
    return !!this.startSubmitting()[activityTypeId];
  }

  getRowDurationLabel(row: GroupRow): string {
    return row.startDurationMinutes === null ? 'No duration set' : `${row.startDurationMinutes} min`;
  }

  getDaysSinceAriaLabel(row: GroupRow): string {
    if (row.daysSince === null) {
      return 'last done never';
    }
    if (row.daysSince === 0) {
      return 'last done today';
    }
    return `last done ${row.daysSince} days ago`;
  }

  isRunningRow(row: GroupRow): boolean {
    return this.isRunningGroup() && this.runningBlock()?.activityTypeId === row.activityTypeId;
  }

  expandInlineTimer(): void {
    this.setTimerExpandedPreference(true);
    this.expandedInlineTimer.set(true);
    this.expandedRemoteTimer.set(false);
  }

  collapseInlineTimer(): void {
    this.setTimerExpandedPreference(false);
    this.expandedInlineTimer.set(false);
    setTimeout(() => this.focusInlineTimerExpandButton());
  }

  expandRemoteTimer(): void {
    this.setTimerExpandedPreference(true);
    this.expandedRemoteTimer.set(true);
    this.expandedInlineTimer.set(false);
  }

  collapseRemoteTimer(): void {
    this.setTimerExpandedPreference(false);
    this.expandedRemoteTimer.set(false);
    setTimeout(() => this.focusRemoteTimerExpandButton());
  }

  completeRunningBlock(): void {
    this.store.completeRunningBlock();
  }

  pauseRunningBlock(): void {
    this.store.pauseRunningBlock();
  }

  resumeRunningBlock(): void {
    this.store.resumeRunningBlock();
  }

  onAutoAdvance(): void {
    this.store.onAutoAdvance();
  }

  skipRunningBlock(reason: string): void {
    this.store.skipRunningBlock(reason);
  }

  refreshTimerError(): void {
    this.store.refreshTimerError(() => this.blockMutationError.set(null));
  }

  getActivityName(activityTypeId: string): string {
    return this.store.getActivityName(activityTypeId);
  }

  runningActivityQueryParams(): Record<string, string> | null {
    const runningOrdinal = this.runningScheduleType();
    return runningOrdinal ? { from: runningOrdinal } : null;
  }

  runningGroupCommands(): string[] {
    const runningOrdinal = this.runningScheduleType();
    return runningOrdinal ? groupPageCommands(runningOrdinal) : ['/today'];
  }

  groupCommands(ordinal: AllocationRow['scheduleType']): string[] {
    return groupPageCommands(ordinal);
  }

  formatElapsed(value: number): string {
    return formatElapsedMs(value);
  }

  onOpenDetailsFromMenu(event: Event, activityTypeId: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.closeMenu(false);
    this.navigateToAllocation(activityTypeId);
  }

  onMarkNotThisCycleFromMenu(event: Event, activityTypeId: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.closeMenu(false);
    if (!this.store.confirmNotThisCycle(activityTypeId)) {
      return;
    }
    this.store.markNotThisCycle(activityTypeId);
  }

  undoNotThisCycle(event: Event, activityTypeId: string): void {
    event.stopPropagation();
    event.preventDefault();
    this.store.clearNotThisCycle(activityTypeId);
  }

  isNotThisCycleSubmitting(activityTypeId: string): boolean {
    return this.store.isNotThisCycleSubmitting(activityTypeId);
  }

  getSetAsideMetaLabel(): string {
    const group = this.selectedGroup();
    if (!group) {
      return '';
    }
    const cycle = resolveScheduleGroupCycle(group);
    const label = resolveCycleDisplayLabel(cycle, this.workCycleLabel(), this.lifeCycleLabel());
    return `Set aside until the ${label} cycle resets`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const openId = this.openMenuActivityId();
    if (!openId) {
      return;
    }
    const anchor = this.document.querySelector(`[data-menu-anchor="${openId}"]`);
    if (anchor instanceof HTMLElement && !anchor.contains(event.target as Node)) {
      this.closeMenu();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    const openId = this.openMenuActivityId();
    if (!openId) {
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeMenu();
      return;
    }
    if (event.key === 'Tab') {
      this.closeMenu(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.focusMenuItem(openId);
    }
  }

  private applyOrdinalFromParam(param: string | null): void {
    const parsed = parseGroupOrdinal(param);
    if (!parsed) {
      void this.router.navigate(['/today'], { replaceUrl: true });
      return;
    }
    this.groupOrdinal.set(parsed);
    this.ensureSelectedGroupEnabled();
    this.syncExpandedTimerPreference();
    this.syncRunningTimerTicker();
    this.applyForcedTimerOpenIfPossible();
  }

  private ensureSelectedGroupEnabled(): void {
    if (!this.loaded) {
      return;
    }
    if (!this.selectedGroup()) {
      void this.router.navigate(['/today'], { replaceUrl: true });
    }
  }

  private syncRunningTimerTicker(): void {
    const runningBlock = this.runningBlock();
    if (!runningBlock) {
      this.elapsedMs.set(0);
      this.stopElapsedTicker();
      this.forcedTimerOpenForVisit = false;
      this.expandedInlineTimer.set(false);
      this.expandedRemoteTimer.set(false);
      return;
    }
    this.updateElapsed();
    if (runningBlock.pausedAt) {
      this.stopElapsedTicker();
    } else {
      this.startElapsedTicker();
    }
  }

  private startElapsedTicker(): void {
    this.stopElapsedTicker();
    this.elapsedIntervalId = setInterval(() => this.updateElapsed(), 1000);
  }

  private stopElapsedTicker(): void {
    if (this.elapsedIntervalId !== null) {
      clearInterval(this.elapsedIntervalId);
      this.elapsedIntervalId = null;
    }
  }

  private updateElapsed(): void {
    const runningBlock = this.runningBlock();
    if (!runningBlock) {
      this.elapsedMs.set(0);
      return;
    }
    this.elapsedMs.set(computeActiveElapsedMs(
      {
        startedAt: runningBlock.startedAt,
        pausedAt: runningBlock.pausedAt,
        accumulatedPauseMs: runningBlock.accumulatedPauseMs
      },
      Date.now()
    ));
  }

  private applyForcedTimerOpenIfPossible(): void {
    if (!this.pendingTimerOpenFromQuery || !this.loaded) {
      return;
    }
    if (!this.runningBlock()) {
      this.pendingTimerOpenFromQuery = false;
      this.stripTimerQueryParam();
      return;
    }
    this.forcedTimerOpenForVisit = true;
    if (this.isRunningGroup()) {
      this.expandedInlineTimer.set(true);
      this.expandedRemoteTimer.set(false);
    } else {
      this.expandedRemoteTimer.set(true);
      this.expandedInlineTimer.set(false);
    }
    this.pendingTimerOpenFromQuery = false;
    this.stripTimerQueryParam();
    this.scrollRunningIntoView();
  }

  private stripTimerQueryParam(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { timer: null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private syncExpandedTimerPreference(): void {
    const runningBlock = this.runningBlock();
    if (!runningBlock) {
      this.expandedInlineTimer.set(false);
      this.expandedRemoteTimer.set(false);
      return;
    }
    if (this.forcedTimerOpenForVisit) {
      if (this.isRunningGroup()) {
        this.expandedInlineTimer.set(true);
        this.expandedRemoteTimer.set(false);
      } else {
        this.expandedRemoteTimer.set(true);
        this.expandedInlineTimer.set(false);
      }
      return;
    }
    if (this.isRunningGroup()) {
      this.expandedInlineTimer.set(this.timerExpandedPreference);
      this.expandedRemoteTimer.set(false);
    } else {
      this.expandedRemoteTimer.set(this.timerExpandedPreference);
      this.expandedInlineTimer.set(false);
    }
  }

  private setTimerExpandedPreference(expanded: boolean): void {
    this.forcedTimerOpenForVisit = false;
    this.timerExpandedPreference = expanded;
    try {
      localStorage.setItem(this.timerPreferenceKey, expanded ? 'true' : 'false');
    } catch {
      // Ignore localStorage access failures.
    }
  }

  private loadTimerPreference(): void {
    try {
      this.timerExpandedPreference = localStorage.getItem(this.timerPreferenceKey) === 'true';
    } catch {
      this.timerExpandedPreference = false;
    }
  }

  private scrollRunningIntoView(): void {
    setTimeout(() => {
      const targetId = this.isRunningGroup() ? this.runningRowAnchorId : this.runningStripAnchorId;
      this.document.getElementById(targetId)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }

  private focusMenuTrigger(activityTypeId: string): void {
    const trigger = this.document.querySelector(`[data-menu-trigger="${activityTypeId}"]`);
    if (trigger instanceof HTMLElement) {
      trigger.focus();
    }
  }

  private focusMenuItem(activityTypeId: string): void {
    const item = this.document.querySelector(`[data-menu-item="${activityTypeId}"]`);
    if (item instanceof HTMLElement) {
      item.focus();
    }
  }

  private focusInlineTimerExpandButton(): void {
    const button = this.document.querySelector(this.inlineTimerExpandButtonSelector);
    if (button instanceof HTMLElement) {
      button.focus();
    }
  }

  private focusRemoteTimerExpandButton(): void {
    const button = this.document.querySelector(this.remoteTimerExpandButtonSelector);
    if (button instanceof HTMLElement) {
      button.focus();
    }
  }
}
